import { call } from '@advanza/api'
import { changeEntity } from 'actions/entities'
import { _changeFilter, _fetchItems } from 'actions/lists'
import { AppThunkAction } from 'hooks/hooks'
import aggregatedReviewsSchema from 'schemes/aggregatedReviewsSchema'

function todayDateString(): string {
    return new Date().toISOString().slice(0, 10)
}

export const AGGREGATED_REVIEWS_RECEIVE_LIST = 'AGGREGATED_REVIEWS_RECEIVE_LIST'
export const AGGREGATED_REVIEWS_CHANGE_FILTER = 'AGGREGATED_REVIEWS_CHANGE_FILTER'
export const AGGREGATED_REVIEWS_REQUEST = 'AGGREGATED_REVIEWS_REQUEST'
export const AGGREGATED_REVIEWS_RECEIVE_SOURCES = 'AGGREGATED_REVIEWS_RECEIVE_SOURCES'

export function fetchAggregatedReviews(filterId: string) {
    return _fetchItems(filterId, {
        url: 'office/aggregated-reviews/get-list',
        schema: aggregatedReviewsSchema,
        requestActionName: AGGREGATED_REVIEWS_REQUEST,
        receiveActionName: AGGREGATED_REVIEWS_RECEIVE_LIST,
        reducerKey: 'aggregatedReviews',
    })
}

export function changeAggregatedReviewsFilter(filterId: string, options: Record<string, unknown>) {
    return _changeFilter(filterId, options, {
        reducerKey: 'aggregatedReviews',
        fetchFunc: fetchAggregatedReviews,
        actionType: AGGREGATED_REVIEWS_CHANGE_FILTER,
    })
}

export function fetchAllSources(forceFetch?: boolean): AppThunkAction<Promise<unknown>> {
    return (dispatch, getState) => {
        if (getState().aggregatedReviews.sources.length > 0 && !forceFetch) {
            return Promise.resolve(undefined)
        }
        return call('office/aggregated-reviews/get-all-sources').then(
            (response: { sources?: unknown[] }) => {
                dispatch({ type: AGGREGATED_REVIEWS_RECEIVE_SOURCES, ...response })
            },
        )
    }
}

export function deleteAggregatedReview(id: string | number): AppThunkAction<Promise<unknown>> {
    return (dispatch) => {
        return call(`office/aggregated-reviews/delete/${id}`, { method: 'post' }).then(() => {
            dispatch({ type: 'INVALIDATE_AGGREGATED_REVIEWS' })
        })
    }
}

export function saveAggregatedReview(id: string | number): AppThunkAction<Promise<unknown>> {
    return (dispatch, getState) => {
        const { aggregatedReviews, aggregatedReviewsSnippets } =
            getState().aggregatedReviews.entities
        const entity = aggregatedReviews[id as keyof typeof aggregatedReviews]
        const isNew = typeof id === 'string'
        return call(`office/aggregated-reviews/save/${isNew ? '' : id}`, {
            json: {
                ...entity,
                aggregated_reviews_snippets: entity.aggregated_reviews_snippets.map(
                    (snippetId: string) =>
                        aggregatedReviewsSnippets[
                            snippetId as keyof typeof aggregatedReviewsSnippets
                        ],
                ),
            },
        }).then(() => {
            dispatch({ type: 'INVALIDATE_AGGREGATED_REVIEWS' })
        })
    }
}

export function deleteSnippet(id: string | number): AppThunkAction<Promise<unknown>> {
    return (dispatch, getState) => {
        const isNew = typeof id === 'string'
        const { aggregatedReviews, aggregatedReviewsSnippets } =
            getState().aggregatedReviews.entities
        if (isNew) {
            const entity = aggregatedReviewsSnippets[id as keyof typeof aggregatedReviewsSnippets]
            const aggregatedReview =
                aggregatedReviews[entity.aggregated_review_id as keyof typeof aggregatedReviews]
            dispatch(
                changeEntity({
                    store: 'aggregatedReviews',
                    name: 'aggregatedReviews',
                    key: aggregatedReview.aggregated_review_id,
                    diff: {
                        aggregated_reviews_snippets:
                            aggregatedReview.aggregated_reviews_snippets.filter(
                                (id1: string) => id !== id1,
                            ),
                    },
                    filterId: undefined,
                    remove: undefined,
                    newKey: undefined,
                    dontTouch: undefined,
                    preservePreviousTouch: undefined,
                }),
            )
            return Promise.resolve(undefined)
        }

        return call(`office/aggregated-reviews/delete-snippet/${id}`, { method: 'post' }).then(
            () => {
                dispatch({ type: 'INVALIDATE_AGGREGATED_REVIEWS' })
            },
        )
    }
}

export function addSnippet(id: string | number): AppThunkAction<void> {
    return (dispatch, getState) => {
        const { aggregatedReviews } = getState().aggregatedReviews.entities
        const entity = aggregatedReviews[id as keyof typeof aggregatedReviews]
        const newId = Math.random().toString(36).substr(2, 5)
        dispatch(
            changeEntity({
                store: 'aggregatedReviews',
                name: 'aggregatedReviewsSnippets',
                key: newId,
                diff: {
                    snippet: 'test',
                    aggregated_review_id: id,
                    review_date: todayDateString(),
                    first_name: 'test',
                    _open: true,
                },
                filterId: undefined,
                remove: undefined,
                newKey: undefined,
                dontTouch: undefined,
                preservePreviousTouch: undefined,
            }),
        )
        dispatch(
            changeEntity({
                store: 'aggregatedReviews',
                name: 'aggregatedReviews',
                key: id,
                diff: {
                    aggregated_reviews_snippets: entity.aggregated_reviews_snippets.concat(newId),
                },
                filterId: undefined,
                remove: undefined,
                newKey: undefined,
                dontTouch: undefined,
                preservePreviousTouch: undefined,
            }),
        )
    }
}

export interface AddAggregatedReviewOptions {
    providerId: number
    filterId: string
    [key: string]: unknown
}

export function addAggregatedReview(options: AddAggregatedReviewOptions): AppThunkAction<void> {
    return (dispatch) => {
        const { providerId, filterId, ...rest } = options
        const newId = Math.random().toString(36).substr(2, 5)
        dispatch(
            changeEntity({
                store: 'aggregatedReviews',
                name: 'aggregatedReviews',
                filterId,
                key: newId,
                diff: {
                    service_provider_id: providerId,
                    aggregated_reviews_source: {},
                    aggregated_reviews_snippets: [],
                    _open: true,
                    nr_of_5: 0,
                    nr_of_4: 0,
                    nr_of_3: 0,
                    nr_of_2: 0,
                    nr_of_1: 0,
                    ...rest,
                },
                remove: undefined,
                newKey: undefined,
                dontTouch: undefined,
                preservePreviousTouch: undefined,
            }),
        )
    }
}
